/**
 * Who can open client records: the browser twin of
 * api/_shared/clientRecordAccess.ts, plus the owner card's copy.
 *
 * The browser never sees tenants.config: it reads the setting only from
 * whoami (client_record_access, combined by canReadEveryClient in
 * capabilities.ts) and from the card's own GET. This module carries the
 * type, the two values, the default and the role partition, kept equal to
 * the server module element for element by
 * tests/ui/capabilityPredicates.test.ts (api/ is a separate package, so the
 * two are twins rather than one import).
 */

import type { UserTenantRole } from './capabilities';

export type ClientRecordAccess = 'all_staff' | 'own_service';

export const CLIENT_RECORD_ACCESS_VALUES: readonly ClientRecordAccess[] = ['all_staff', 'own_service'] as const;

export const DEFAULT_CLIENT_RECORD_ACCESS: ClientRecordAccess = 'own_service';

export function isClientRecordAccess(v: unknown): v is ClientRecordAccess {
  return v === 'all_staff' || v === 'own_service';
}

/** The six internal roles the setting applies to. */
export const INTERNAL_STAFF_ROLES: readonly UserTenantRole[] = [
  'owner',
  'data_steward',
  'exec_support',
  'supervising_cm',
  'admin_support',
  'primary_cm',
] as const;

/** The external seats the setting never applies to. */
export const EXTERNAL_SEAT_ROLES: readonly UserTenantRole[] = ['board_observer', 'mso_staff', 'accounting'] as const;

/** Roles refused a Directory entry's connected clients although they read
 *  every client: the billing seat reads the roster for billing, and who a
 *  directory contact is connected to is not billing. The board keeps them. */
export const CONNECTED_CLIENTS_FENCED_ROLES: readonly UserTenantRole[] = ['accounting'] as const;

export function isConnectedClientsFenced(role: string | null | undefined): boolean {
  return typeof role === 'string' && (CONNECTED_CLIENTS_FENCED_ROLES as readonly string[]).includes(role);
}

/** Internal staff who do no client work: the setting never applies to them. */
export const NON_CLINICAL_STAFF_ROLES: readonly UserTenantRole[] = ['directory_editor'] as const;

export function isInternalStaffRole(role: string | null | undefined): boolean {
  return typeof role === 'string' && (INTERNAL_STAFF_ROLES as readonly string[]).includes(role);
}

/**
 * The card's words (Admin › Permissions, above the matrix). The
 * Permissions matrix's lane blurb is built from the heading and the first
 * option's label, so a copy change moves both.
 */
export const CLIENT_RECORD_ACCESS_COPY = {
  heading: 'Who can open client records',
  options: {
    all_staff: {
      label: 'Everyone on staff',
      subline:
        'Every internal staff member can open any client’s record and sees every client on the roster, the maps and the client lookups. They can add notes and add, edit or remove record entries for any client, and edit or remove their own notes. Changing someone else’s note needs Edit or remove anyone’s clinical notes, below. It does not apply to a Directory Editor.',
    },
    own_service: {
      label: 'Each person’s own service',
      subline:
        'Care managers open care-management clients, SPICE therapists open SPICE clients, and anyone opens a client whose care team they are on. Office seats always see every client.',
      help: 'Care management or SPICE, from the staff record',
      screens: 'Each person sees only clients in their own service, including on My time and My Clients.',
    },
  },
  help: 'Takes effect right away. Menus and links update the next time someone opens or refreshes Olive.',
  unrecognised:
    'The saved setting was not recognised, so Olive is using Each person’s own service. Choose an option to fix it.',
  undoLabel: (label: string): string => `Client records: ${label}.`,
} as const;
